import { useEffect, useRef, useState } from "react";

const useServerSentEvents = (url, onMessage, options = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [lastEventTime, setLastEventTime] = useState(null);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const {
    maxReconnectAttempts = 5,
    reconnectInterval = 3000,
    withCredentials = true,
    conflictDetector = null,
  } = options;

  const connect = () => {
    try {
      eventSourceRef.current = new EventSource(url, { withCredentials });

      eventSourceRef.current.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
      };

      eventSourceRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastEventTime(new Date());

          // Check for edit conflicts if conflict detector is provided
          let conflict = null;
          if (
            conflictDetector &&
            data.type &&
            data.entityType &&
            data.entityId
          ) {
            conflict = conflictDetector.checkForConflicts(data.type, {
              entityType: data.entityType,
              entityId: data.entityId,
              newData: data.data || data,
              updateTimestamp: data.timestamp || Date.now(),
            });
          }

          // Pass both original data and conflict info to message handler
          onMessage(data, conflict);
        } catch (err) {
          console.error("Error parsing SSE message:", err);
        }
      };

      eventSourceRef.current.onerror = () => {
        setIsConnected(false);

        // Attempt to reconnect
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          eventSourceRef.current.close();
          reconnectTimeoutRef.current = setTimeout(
            connect,
            reconnectInterval * reconnectAttempts.current,
          );
        } else {
          setError("Connection lost");
        }
      };
    } catch (err) {
      setError("Failed to connect");
    }
  };

  const disconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
  };

  useEffect(() => {
    connect();
    return disconnect;
  }, [url]);

  return { isConnected, error, lastEventTime, disconnect };
};

export default useServerSentEvents;
